<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useBranding } from 'shared/composables/useBranding';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import UserBasicDetails from './UserBasicDetails.vue';
import MessageSignature from './MessageSignature.vue';
import FontSize from './FontSize.vue';
import UserLanguageSelect from './UserLanguageSelect.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';

const { t } = useI18n();
const store = useStore();
const { isEditorHotKeyEnabled, updateUISettings } = useUISettings();
const { currentFontSize, updateFontSize } = useFontSize();
const { replaceInstallationName } = useBranding();
const currentUser = useMapGetter('getCurrentUser');
const currentUserId = useMapGetter('getCurrentUserID');
const globalConfig = useMapGetter('globalConfig/get');

const name = ref('');
const displayName = ref('');
const email = ref('');
const messageSignature = ref('');

const hotKeys = computed(() => [
  {
    key: 'enter',
    title: t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.HEADING'),
    description: t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.CONTENT'),
    lightImage: '/assets/images/dashboard/profile/hot-key-enter.svg',
    darkImage: '/assets/images/dashboard/profile/hot-key-enter-dark.svg',
  },
  {
    key: 'cmd_enter',
    title: t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.HEADING'),
    description: t(
      'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.CONTENT'
    ),
    lightImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter.svg',
    darkImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter-dark.svg',
  },
]);

const initializeUser = () => {
  const user = currentUser.value;
  if (!user?.id) return;
  name.value = user.name || '';
  email.value = user.email || '';
  displayName.value = user.display_name || '';
  messageSignature.value = user.message_signature || '';
};

onMounted(() => {
  if (currentUserId.value) initializeUser();
});

watch(currentUserId, id => {
  if (id) initializeUser();
});

const dispatchUpdate = async (payload, successMessage, errorMessage) => {
  let alertMessage = '';
  try {
    await store.dispatch('updateProfile', payload);
    alertMessage = successMessage;
    return true;
  } catch (error) {
    alertMessage = parseAPIErrorResponse(error) || errorMessage;
    return false;
  } finally {
    useAlert(alertMessage);
  }
};

const updateProfile = async userAttributes => {
  const nextName = userAttributes.name;
  const nextEmail = userAttributes.email;
  const nextDisplayName = userAttributes.displayName;
  const hasEmailChanged = currentUser.value.email !== nextEmail;
  name.value = nextName || name.value;
  email.value = nextEmail || email.value;
  displayName.value = nextDisplayName || displayName.value;

  const success = await dispatchUpdate(
    {
      name: name.value,
      email: email.value,
      displayName: displayName.value,
    },
    hasEmailChanged
      ? t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
      : t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
    t('RESET_PASSWORD.API.ERROR_MESSAGE')
  );

  if (hasEmailChanged && success) clearCookiesOnLogout();
};

const updateSignature = async signature => {
  await dispatchUpdate(
    { message_signature: signature },
    t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'),
    t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR')
  );
};

const toggleHotKey = key => {
  updateUISettings({ editor_message_key: key });
  useAlert(t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.UPDATE_SUCCESS'));
};
</script>

<template>
  <div class="grid">
    <p class="text-sm text-n-slate-11">
      {{ t('PROFILE_SETTINGS.FORM.AVAILABILITY.HUB_HINT') }}
    </p>
    <SectionLayout title="" description="" class="!pt-0">
      <UserBasicDetails
        :name="name"
        :display-name="displayName"
        :email="email"
        :email-enabled="!globalConfig.disableUserProfileUpdate"
        @update-user="updateProfile"
      />
    </SectionLayout>
    <SectionLayout
      with-border
      :title="t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.TITLE')"
      :description="
        replaceInstallationName(
          t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')
        )
      "
    >
      <div class="flex flex-col items-start gap-6">
        <FontSize
          :value="currentFontSize"
          :label="t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.TITLE')"
          :description="
            t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.NOTE')
          "
          @change="updateFontSize"
        />
        <UserLanguageSelect
          :label="t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.TITLE')"
          :description="
            t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.NOTE')
          "
        />
      </div>
    </SectionLayout>
    <SectionLayout
      with-border
      :title="t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
      :description="t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')"
    >
      <MessageSignature
        :message-signature="messageSignature"
        @update-signature="updateSignature"
      />
    </SectionLayout>
    <SectionLayout
      with-border
      :title="t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
      :description="t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.NOTE')"
    >
      <div
        class="flex w-full flex-col justify-between gap-5 sm:flex-row sm:gap-4"
      >
        <RadioCard
          v-for="hotKey in hotKeys"
          :id="hotKey.key"
          :key="hotKey.key"
          :label="hotKey.title"
          :description="hotKey.description"
          :is-active="isEditorHotKeyEnabled(hotKey.key)"
          class="sm:flex-1"
          @select="toggleHotKey"
        >
          <img
            :src="hotKey.lightImage"
            :alt="hotKey.title"
            class="block w-full object-cover dark:hidden"
          />
          <img
            :src="hotKey.darkImage"
            :alt="hotKey.title"
            class="hidden w-full object-cover dark:block"
          />
        </RadioCard>
      </div>
    </SectionLayout>
  </div>
</template>
